/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect } from 'react'
import {
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  ListItem,
  ListItemText,
  Chip,
  Stack,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FORMTYPE, QuizField } from '@services/common.services'
import FormField from '@components/@material-extend/FormField'
import { Controller, useFormContext } from 'react-hook-form'
import AutoCompleteSelect from '@components/@material-extend/SelectWithSearch/AutoCompleteSelect'
import _ from 'lodash'
import CustomTimePicker from '@components/@material-extend/CustomTimePicker'
import dayjs from 'dayjs'
import { StyledTextField } from '@components/@material-extend'
import CustomDatePicker from '@components/@material-extend/CustomDatePicker'
import i18n from '@locales/i18n'
import { shouldShowField } from '@utils/helper/quiz.helper'

// Define the possible form types

export interface QuizOption {
  value: string
  label: string
}

interface Props {
  data: QuizField
  name: string
  error?: any
}

const StepFormFields: React.FC<Props> = ({ data, name, error }) => {
  const { t } = useTranslation()
  const { control, watch, resetField } = useFormContext()

  const formValues = watch()

  const isVisible = shouldShowField(data, formValues)

  useEffect(() => {
    if (formValues[data.field_id]) {
      resetField(data.field_id, formValues[data.field_id])
    }
  }, [])

  const getTranslationOrDefault = (key: string, defaultValue: string) => {
    const fullKey = `QUIZ.${key}`

    return i18n.exists(fullKey) ? t(fullKey) : defaultValue
  }

  const renderField = () => {
    if (!isVisible) return null
    const label = getTranslationOrDefault(data.question, data.question)
    const helperText = data.formHelp ? t(`QUIZ.${data.formHelp}`) : undefined
    const desc = data.description ? t(`QUIZ.${data.description}`) : undefined
    const options = data.options ? data.options : []

    switch (data.form_type) {
      case FORMTYPE.SELECT:
        return (
          <Controller
            name={name}
            control={control}
            rules={data.validation_rule || ({} as any)}
            render={({ field: { value, onChange } }) => (
              <FormField
                sx={{ mb: 1 }}
                fullWidth
                errors={error}
                label={label}
                desc={desc}
                helpContent={helperText}
                required
              >
                <Box sx={{ width: '100%' }}>
                  <AutoCompleteSelect
                    fullWidth
                    value={value}
                    onInputChange={(value) => onChange(value)}
                    data={data.options as any}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    getOptionLabel={(option) =>
                      getTranslationOrDefault(option.label, option.label) || ''
                    }
                    disablePortal={false}
                    renderOption={(props, option) => {
                      return (
                        <ListItem component="li" {...props} key={option.value}>
                          <ListItemText sx={{ display: 'block' }}>
                            {getTranslationOrDefault(
                              option.label,
                              option.label
                            )}
                          </ListItemText>
                        </ListItem>
                      )
                    }}
                  />
                </Box>
              </FormField>
            )}
          />
        )

      case FORMTYPE.MULTI_SELECT:
        return (
          <Controller
            name={name}
            control={control}
            rules={data.validation_rule || ({} as any)}
            render={({ field: { value, onChange } }) => (
              <FormField
                sx={{ mb: 1 }}
                fullWidth
                errors={error}
                label={label}
                desc={desc}
                helpContent={helperText}
                required
              >
                <AutoCompleteSelect
                  multiple
                  fullWidth
                  value={value ? value : []}
                  onInputChange={(value) => onChange(value)}
                  data={data.options as any}
                  disablePortal={false}
                  getOptionLabel={(option) =>
                    getTranslationOrDefault(option.label, option.label) || ''
                  }
                  renderOption={(props, option) => {
                    return (
                      <ListItem component="li" {...props} key={option.value}>
                        <ListItemText sx={{ display: 'block' }}>
                          {getTranslationOrDefault(option.label, option.label)}
                        </ListItemText>
                      </ListItem>
                    )
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                />
              </FormField>
            )}
          />
        )

      case FORMTYPE.CHIP_SELECT:
        return (
          <Controller
            name={name}
            control={control}
            rules={data.validation_rule || ({} as any)}
            render={({ field: { value, onChange } }) => (
              <FormField
                sx={{ mb: 1 }}
                fullWidth
                errors={error}
                label={label}
                desc={desc}
                helpContent={helperText}
                required
              >
                <Stack
                  mt={2}
                  direction={'row'}
                  alignItems={'flex-start'}
                  flexWrap={'wrap'}
                >
                  {data &&
                    options.map((option) => (
                      <Chip
                        key={option.value}
                        label={getTranslationOrDefault(
                          option.label,
                          option.label
                        )}
                        sx={{ mr: 1, mb: 1 }}
                        onClick={() => onChange(option)}
                        color={
                          value.value === option.value ? 'primary' : 'default'
                        }
                        variant={
                          value.value === option.value ? 'filled' : 'outlined'
                        }
                        className={`cursor-pointer transition-all duration-200 ${
                          value.value === option.value
                            ? 'shadow-sm'
                            : 'hover:bg-gray-50'
                        }`}
                      />
                    ))}
                </Stack>
              </FormField>
            )}
          />
        )

      case FORMTYPE.CHIP_MULTI_SELECT:
        return (
          <Controller
            name={name}
            control={control}
            rules={data.validation_rule || ({} as any)}
            render={({ field: { value, onChange } }) => (
              <FormField
                sx={{ mb: 1 }}
                fullWidth
                errors={error}
                label={label}
                desc={desc}
                helpContent={helperText}
                required
              >
                <Stack
                  mt={2}
                  direction={'row'}
                  alignItems={'flex-start'}
                  flexWrap={'wrap'}
                >
                  {data &&
                    options.map((option) => {
                      // Check if the option is in the selected values array
                      const isSelected =
                        _.isArray(value) &&
                        value?.some((selected: QuizOption) =>
                          _.isEqual(selected, option)
                        )

                      return (
                        <Chip
                          key={option.label}
                          label={getTranslationOrDefault(
                            option.label,
                            option.label
                          )}
                          sx={{ mr: 1, mb: 1 }}
                          onClick={() => {
                            if (isSelected) {
                              // Remove option if already selected
                              onChange(
                                _.isArray(value)
                                  ? value.filter(
                                      (item: QuizOption) =>
                                        !_.isEqual(item, option)
                                    )
                                  : []
                              )
                            } else {
                              // Add option if not selected
                              onChange([...(value || []), option])
                            }
                          }}
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          className={`cursor-pointer transition-all duration-200 ${
                            isSelected ? 'shadow-sm' : 'hover:bg-gray-50'
                          }`}
                        />
                      )
                    })}
                </Stack>
              </FormField>
            )}
          />
        )

      case FORMTYPE.DATE_PICKER:
        return (
          <Controller
            name={name}
            control={control}
            rules={data.validation_rule || ({} as any)}
            render={({ field: { value, onChange, ...rest } }) => (
              <FormField
                sx={{ mb: 1 }}
                errors={error}
                fullWidth
                label={label}
                desc={desc}
                helpContent={helperText}
                required
              >
                <CustomDatePicker
                  error={false}
                  value={value ? value : dayjs()}
                  onChange={(newDates) => {
                    onChange(newDates)
                  }}
                  {...rest}
                />
              </FormField>
            )}
          />
        )

      case FORMTYPE.TIMEPICKER:
        return (
          <Controller
            name={name}
            control={control}
            rules={data.validation_rule || ({} as any)}
            render={({ field: { value, onChange, ref, ...rest } }) => (
              <FormField
                sx={{ mb: 1, maxWidth: '100%' }}
                fullWidth
                errors={error}
                label={label}
                desc={desc}
                helpContent={helperText}
                required
              >
                <CustomTimePicker
                  error={false}
                  value={value ? dayjs(value) : null}
                  onChange={onChange}
                  format="HH:mm"
                  {...rest}
                />
              </FormField>
            )}
          />
        )

      case FORMTYPE.NUMBER:
        return (
          <Controller
            name={name}
            control={control}
            rules={data.validation_rule || ({} as any)}
            render={({ field: { value, onChange, ref, ...rest } }) => (
              <FormField
                sx={{ mb: 1, width: '100%' }}
                errors={error}
                label={label}
                desc={desc}
                fullWidth
                helpContent={helperText}
                required
              >
                <Box>
                  <StyledTextField
                    inputProps={{ min: 1, step: 1 }}
                    inputRef={ref}
                    value={value}
                    sx={{ maxWidth: 220, width: '100%', margin: '0 auto' }}
                    onChange={onChange}
                    type="number"
                    required
                    {...rest}
                  />
                </Box>
              </FormField>
            )}
          />
        )
      default:
        return null
    }
  }

  return <Box sx={{ width: '100%' }}>{renderField()}</Box>
}

export default StepFormFields
